import { type FC, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import Switch from '@/components/UI/FormElements/Switch';
import Banner from '@/components/UI/Banner/Banner';
import DraggableList, { asSortableListItem } from '@/components/UI/List/DraggableList';
import {
  DEFAULT_WORKFLOW_INTAKE,
  isAnyIntakeEnabled,
} from '@/components/Workflow/Show/Views/Consumption/Consumption.utils';
import { _notNil } from '@/littledash';
import { type ConsumptionType } from '@/model/Consumption.model.ts';
import { RouteService } from '@/support/RouteService';
import {
  WORKFLOW_INTAKE_FORM_PATH,
  WORKFLOW_TAB_INDEX,
  workflowTabPath,
} from '@/components/Modals/SetupWorkflow/SetupWorkflow.utils';
import type { SetupWorkflowFormValues } from '@/components/Modals/SetupWorkflow/SetupWorkflow';

const consumptionPath = workflowTabPath(WORKFLOW_TAB_INDEX.CONSUMPTION);

type IntakeEnabledFieldName = (typeof WORKFLOW_INTAKE_FORM_PATH)['foodEnabled' | 'fluidEnabled'];

interface CheckboxOption {
  id: ConsumptionType;
  name: string;
  fieldName: IntakeEnabledFieldName;
  label: string;
}

interface ActionsBarProps {
  hasSelection: boolean;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

const INTAKE_OPTION_BY_TYPE: Record<ConsumptionType, CheckboxOption> = {
  fluid: {
    id: 'fluid',
    name: 'enable-fluid-intake',
    fieldName: WORKFLOW_INTAKE_FORM_PATH.fluidEnabled,
    label: 'Fluid intake',
  },
  food: {
    id: 'food',
    name: 'enable-food-intake',
    fieldName: WORKFLOW_INTAKE_FORM_PATH.foodEnabled,
    label: 'Food intake',
  },
};

const ALL_INTAKE_OPTIONS = Object.values(INTAKE_OPTION_BY_TYPE);

const InfoBanner: FC = () => (
  <Banner info dismiss={false}>
    <h3 className="f6 normal lh-title pb1">
      Food and fluid intake metrics can be backdated, provided no later metric exists for the same cage.
      <a
        target="_blank"
        rel="noopener noreferrer"
        className="dib link blue ml1"
        href={RouteService.help('food_fluid_tracking')}
      >
        Read more
      </a>
    </h3>
  </Banner>
);

const ActionsBar: FC<ActionsBarProps> = ({ hasSelection, onSelectAll, onClearSelection }) => (
  <div className="pa3 pb0">
    <div className="pb3">
      <a className="dib link blue f7 mr3" onClick={onSelectAll}>
        Select all
      </a>
      <a
        className={cn('dib f7', {
          'link blue': hasSelection,
          'pointer-events-none': !hasSelection,
        })}
        onClick={onClearSelection}
      >
        Clear selection
      </a>
    </div>
  </div>
);

const CheckboxField: FC<CheckboxOption> = ({ name, fieldName, label }) => {
  const { control } = useFormContext<SetupWorkflowFormValues>();

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { value, onChange } }) => {
        const isChecked = value === true;
        return <Checkbox name={name} checked={isChecked} label={label} onChange={() => onChange(!isChecked)} />;
      }}
    />
  );
};

const RecordingTimeSetting: FC = () => {
  const { control } = useFormContext<SetupWorkflowFormValues>();

  return (
    <div className="pa3 bt b--moon-gray flex justify-between items-center flex-wrap align-center">
      <div>
        <label className="mb1 f6">Set recording time</label>
        <small>Select a custom time for recording food and fluid measurements.</small>
      </div>
      <Controller
        name={WORKFLOW_INTAKE_FORM_PATH.timeEnabled}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Switch value={value ?? false} onChange={onChange} testId="enable-intake-time-switch" />
        )}
      />
    </div>
  );
};

const AFTER_SAVE_OPTIONS = [
  { value: 'focus-search', testId: 'after-saving-option-focus-search-bar', label: 'Focus search bar' },
  { value: 'first-subject', testId: 'after-saving-option-move-to-first-animal', label: 'Move to first animal' },
  { value: 'next-cage', testId: 'after-saving-option-move-to-next-cage', label: 'Move to next cage' },
] as const;

const AfterSaveSetting: FC = () => {
  const { control } = useFormContext<SetupWorkflowFormValues>();

  return (
    <div
      className="pa3 bt b--moon-gray flex justify-between items-center"
      data-testid="workflow-consumption-after-save-select"
    >
      <label className="mb0 f6">After saving</label>
      <Controller
        defaultValue="focus-search"
        name={`${consumptionPath}.onSave`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <select className="mw5 mb0" value={value} onChange={onChange}>
            {AFTER_SAVE_OPTIONS.map(({ value: optionValue, testId, label }) => (
              <option key={optionValue} value={optionValue} data-testid={testId}>
                {label}
              </option>
            ))}
          </select>
        )}
      />
    </div>
  );
};

const WorkflowConsumption: FC = () => {
  const { control, setValue } = useFormContext<SetupWorkflowFormValues>();
  const intake = useWatch({ control, name: WORKFLOW_INTAKE_FORM_PATH.intake }) ?? DEFAULT_WORKFLOW_INTAKE;
  const hasIntakeSelection = isAnyIntakeEnabled(intake);

  const orderedOptions = useMemo(() => {
    const ordered = intake.order.reduce<Array<CheckboxOption>>((acc, type) => {
      const option = INTAKE_OPTION_BY_TYPE[type];
      if (_notNil(option)) {
        acc.push(option);
      }
      return acc;
    }, []);
    const missing = ALL_INTAKE_OPTIONS.filter((option) => !intake.order.includes(option.id));
    return [...ordered, ...missing];
  }, [intake.order]);

  const handleReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      const nextOrder = [...intake.order];
      const [moved] = nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(destinationIndex, 0, moved);
      setValue(WORKFLOW_INTAKE_FORM_PATH.order, nextOrder, { shouldDirty: true });
    },
    [intake.order, setValue]
  );

  const setAllIntake = (enabled: boolean) => {
    setValue(WORKFLOW_INTAKE_FORM_PATH.foodEnabled, enabled, { shouldDirty: true });
    setValue(WORKFLOW_INTAKE_FORM_PATH.fluidEnabled, enabled, { shouldDirty: true });
  };

  return (
    <div className="flex flex-column justify-between h-100">
      <div>
        <InfoBanner />
        <ActionsBar
          hasSelection={hasIntakeSelection}
          onSelectAll={() => setAllIntake(true)}
          onClearSelection={() => setAllIntake(false)}
        />
        <div className="ph3 pb2">
          <DraggableList
            canAmend={false}
            data={orderedOptions}
            sortable={{
              droppableId: 'workflow-setup-consumption-intake',
              onReorder: handleReorder,
            }}
            renderItem={asSortableListItem({ canSort: true, itemClassName: 'pv2' })(CheckboxField)}
          />
        </div>
      </div>
      <div>
        <RecordingTimeSetting />
        <AfterSaveSetting />
      </div>
    </div>
  );
};

export default WorkflowConsumption;
